import { useCallback, useEffect, useRef, useState } from 'react'
import type { AnchorHTMLAttributes, JSX, ReactNode } from 'react'
import {
  ApiError,
  fetchAccountOnboarding,
  fetchAccounts,
  fetchDeployment,
  fetchDeployments,
  fetchFleet,
  fetchMe,
  fetchQrveyFleet,
  fetchRun,
  fetchTenants,
  NotAuthenticated,
} from './api.js'
import { assertNever, isRole, roleHasCapability } from '@qim/shared'
import {
  beginSignIn,
  completeSignIn,
  currentToken,
  mayAttemptAutomatically,
  onSessionEnded,
  SignInFailed,
} from './auth/session.js'
import { pathFor, viewFor } from './routing.js'
import { subnavFor } from './tabs.js'
import { Access } from './views/access.jsx'
import { Account, AccountSection } from './views/account.jsx'
import { ApprovalsPanel } from './views/approvals.jsx'
import { ProtectionLock } from './views/lock.jsx'
import { Audit } from './views/audit.jsx'
import { Customer, CustomerCost } from './views/customer.jsx'
import { FleetCostTab } from './views/fleet-cost.jsx'
import { Subnav, TabUnavailable } from './views/subnav.jsx'
import { CustomerUsersPanel } from './views/users.jsx'
import { Customers } from './views/customers.jsx'
import { Fleet } from './views/fleet.jsx'
import { AccountLink } from './views/account-link.jsx'
import { Palette } from './views/palette.jsx'
import { Run } from './views/run.jsx'
import { Deployment } from './views/deployment.jsx'
import type { AppConfig } from './config.js'
import type {
  AccountOnboarding,
  AccountSummary,
  DeploymentDetail,
  DeploymentSummary,
  Me,
  RunSummary,
  TenantSummary,
} from './api.js'
import type { Fleet as FleetData } from '@qim/shared'
import type { AccountTab, CustomerTab, View } from './routing.js'

type State =
  | { readonly status: 'starting' }
  | { readonly status: 'signed-out'; readonly reason?: string }
  | { readonly status: 'signing-in' }
  | { readonly status: 'signed-in'; readonly me: Me }
  | { readonly status: 'error'; readonly message: string }

/**
 * What the current screen needs, once it has it.
 *
 * One shape per screen rather than a bag of optional fields, so rendering a
 * screen without its data is not an expressible state — the same reason the API
 * discriminates its unions.
 */
type Screen =
  | { readonly of: 'loading' }
  | { readonly of: 'fleet'; readonly fleet: FleetData }
  /** The fleet's Cost tab loads its own data, so there is nothing to carry here. */
  | { readonly of: 'fleet-cost' }
  | { readonly of: 'customers'; readonly tenants: readonly TenantSummary[] }
  | {
      readonly of: 'customer'
      readonly tenantId: string
      /**
       * Read for every tab: it is one query of Qrvey's own table, it is what
       * makes a hand-typed tab of a customer out of scope answer "not found"
       * like the rest of the screen, and the Cost tab lists it. What else a tab
       * needs is its own — only Overview reads the deployments.
       */
      readonly accounts: readonly AccountSummary[]
      readonly section:
        | { readonly tab: 'overview'; readonly deployments: readonly DeploymentSummary[] }
        | { readonly tab: Exclude<CustomerTab, 'overview'> }
    }
  /**
   * An account tab other than Onboarding, rendered from the account listing —
   * so opening it makes no cross-account read. See `AccountSection`.
   */
  | {
      readonly of: 'account-section'
      readonly tenantId: string
      readonly tab: Exclude<AccountTab, 'onboarding'>
      readonly account: AccountSummary
    }
  /** A deep link to a tab this viewer's role does not include. */
  | { readonly of: 'unavailable' }
  | {
      readonly of: 'deployment'
      readonly tenantId: string
      readonly deployment: DeploymentDetail
    }
  | {
      readonly of: 'account'
      readonly tenantId: string
      readonly onboarding: AccountOnboarding
    }
  | {
      readonly of: 'run'
      readonly tenantId: string
      readonly runId: string
      readonly run: RunSummary
    }
  /** The audit screen loads its own data, so there is nothing to carry here. */
  | { readonly of: 'audit' }
  /** The access screen loads its own data, so there is nothing to carry here. */
  | { readonly of: 'access' }
  | { readonly of: 'missing' }
  /**
   * The API answered, and it was not a 404.
   *
   * Separate from `missing` because the two send someone to different places:
   * "not found" says check the link, and a 500 says the link was right and
   * something broke behind it. They were the same state until a brand-new
   * account 500'd the onboarding screen and the console said the link might be
   * wrong — which is where Amit went looking (IM-121).
   */
  | { readonly of: 'failed'; readonly status: number }

const describeScope = (scope: Me['scope']): string =>
  scope.kind === 'all'
    ? 'All customers'
    : scope.tenantIds.length === 0
      ? 'No customers assigned'
      : scope.tenantIds.join(', ')

/**
 * A link that navigates in place, without losing cmd-click.
 *
 * The same six lines were written out at each of the console's link sites, and
 * two of them had forgotten the modifier check — so cmd-clicking a breadcrumb
 * swallowed the event and opened nothing. Middle-click and cmd-click open a tab
 * because the href is real; that is the whole reason these are anchors.
 */
const Link = ({
  to,
  onOpen,
  children,
  ...rest
}: {
  readonly to: string
  readonly onOpen: (path: string) => void
  readonly children: ReactNode
} & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href' | 'onClick'>): JSX.Element => (
  <a
    href={to}
    onClick={(event) => {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return
      event.preventDefault()
      onOpen(to)
    }}
    {...rest}
  >
    {children}
  </a>
)

/** Where the Identity Center callback lands; never a place to come back to. */
const CALLBACK_PATH = '/auth/callback'

/**
 * The path and query being viewed, for a sign-in to come back to (IM-170).
 *
 * The callback itself is excluded: coming back to it would land on a page
 * whose only content is a code that has already been spent.
 */
const hereForReturn = (): string =>
  window.location.pathname.startsWith(CALLBACK_PATH)
    ? '/'
    : `${window.location.pathname}${window.location.search}`

const isCallback = (search: string): boolean => {
  const params = new URLSearchParams(search)
  return params.has('code') || params.has('error')
}

const assignLocation = (url: string): void => {
  window.location.assign(url)
}

export const App = ({
  config,
  redirect = assignLocation,
}: {
  config: AppConfig
  /**
   * Leaves for the authorize endpoint. A prop only so a test can see where the
   * console would have gone: jsdom's `location` cannot be spied on or stubbed.
   */
  redirect?: (url: string) => void
}): JSX.Element => {
  const [state, setState] = useState<State>({ status: 'starting' })
  /**
   * Set when a session that existed could not be renewed (IM-170).
   *
   * Separate from `state` so the screen stays mounted: whatever someone was
   * typing survives, and the header says why the next action will fail and
   * offers the way back in, rather than a click failing with nothing said.
   */
  const [sessionEnded, setSessionEnded] = useState(false)
  /**
   * StrictMode runs effects twice in development. Without this the second run
   * would find the first run's loop-guard marker and show Sign in, or redeem
   * a callback code whose verifier the first run has already taken.
   */
  const booted = useRef(false)
  const [view, setView] = useState<View>(() => viewFor(window.location.pathname))
  const [screen, setScreen] = useState<Screen>({ of: 'loading' })
  /**
   * Bumped by a write, so the current screen reloads.
   *
   * A screen that has just created a customer or registered an account must not
   * still be showing the list from before it — and re-fetching is honest in a
   * way that splicing the new record into local state is not, because the API
   * decides what the caller may see.
   */
  const [reloadToken, setReloadToken] = useState(0)
  /*
   * Whether hidden records are shown (IM-112).
   *
   * Held here rather than in a view because it changes what is *fetched* — the
   * API filters, not the browser, so a toggle that only hid rows locally would
   * be a second opinion about what the caller may see. It is in the loader's
   * dependency list, so flipping it re-reads.
   *
   * Not in the URL: it is a momentary "let me look" rather than a place, and a
   * deep link into a ticket should show what everyone else sees.
   */
  const [showHidden, setShowHidden] = useState(false)
  const reload = useCallback(() => setReloadToken((n) => n + 1), [])
  /**
   * The view the current `screen` was loaded for.
   *
   * Only a *navigation* blanks the screen to `loading`. A re-read after a write
   * must leave it mounted, and that is a correctness requirement rather than a
   * matter of flicker: `RegisterAccount` holds the freshly minted external ID in
   * its own state, and that value is shown exactly once with no read path
   * afterwards. Unmounting the screen to reload the list behind it therefore
   * destroyed the only copy of a secret the customer needs — IM-88, and the
   * reason `onRegistered` now fires on dismissal rather than on success.
   */
  const loadedFor = useRef<View | undefined>(undefined)

  /**
   * What the jump-to palette searches (IM-106).
   *
   * Loaded once when signing in rather than per screen, because the palette
   * opens from anywhere and a keystroke must not wait on a request. Both reads
   * are already scoped server-side, so holding them here adds no decision the
   * console has to make.
   *
   * A failure is silent on purpose: the palette is a shortcut, and every
   * destination in it is reachable by navigating. Interrupting someone's screen
   * to say a convenience did not load would be worse than the convenience being
   * briefly empty.
   */
  /*
   * **Unfiltered, deliberately.** The Fleet screen is Qrvey's own instances
   * (`fetchQrveyFleet`), but the palette is how a support engineer jumps to a
   * *customer's* instance, so it keeps the read that spans every kind. Two
   * reads of one route; which one each surface makes is named at its call.
   */
  const [jumpFleet, setJumpFleet] = useState<FleetData | undefined>(undefined)
  const [jumpTenants, setJumpTenants] = useState<readonly TenantSummary[]>([])
  const [paletteOpen, setPaletteOpen] = useState(false)
  /** Where focus was when the palette opened, so Esc gives it back. */
  const cameFrom = useRef<HTMLElement | null>(null)

  /**
   * Navigates without a reload, and keeps the back button working.
   *
   * pushState plus a popstate listener rather than a router library: three
   * screens do not justify the dependency, and the whole of the behaviour is
   * these dozen lines.
   */
  const open = useCallback((path: string) => {
    window.history.pushState({}, '', path)
    setView(viewFor(path))
  }, [])

  useEffect(() => {
    if (state.status !== 'signed-in') return
    let live = true
    void Promise.allSettled([fetchFleet(config.apiBaseUrl), fetchTenants(config.apiBaseUrl)]).then(
      ([fleet, tenants]) => {
        if (!live) return
        if (fleet.status === 'fulfilled') setJumpFleet(fleet.value)
        if (tenants.status === 'fulfilled') setJumpTenants(tenants.value.tenants)
      },
    )
    return () => {
      live = false
    }
  }, [state.status, config.apiBaseUrl, reloadToken])

  /*
   * The one global shortcut. Meta on a Mac, Control elsewhere — reading both
   * rather than sniffing the platform, since either is unambiguous here.
   *
   * Ignored while focus is in a text field, so typing a deployment id into the
   * run form cannot be swallowed by a navigation shortcut.
   */
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== 'k' || !(event.metaKey || event.ctrlKey)) return
      const target = event.target
      const inField =
        target instanceof HTMLElement &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable === true)
      if (inField) return
      event.preventDefault()
      cameFrom.current =
        document.activeElement instanceof HTMLElement ? document.activeElement : null
      setPaletteOpen(true)
    }
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('keydown', onKey)
    }
  }, [])

  /*
   * Focus cannot leave an open modal, so it is returned after the dialog has
   * gone — from an effect, once React has committed the unmount. Calling
   * `focus()` in the close handler silently does nothing (IM-79).
   */
  useEffect(() => {
    if (paletteOpen) return
    const origin = cameFrom.current
    cameFrom.current = null
    origin?.focus()
  }, [paletteOpen])

  useEffect(() => {
    const onPop = (): void => {
      setView(viewFor(window.location.pathname))
    }
    window.addEventListener('popstate', onPop)
    return () => {
      window.removeEventListener('popstate', onPop)
    }
  }, [])

  const load = useCallback(async () => {
    try {
      setState({ status: 'signed-in', me: await fetchMe(config.apiBaseUrl) })
    } catch (error: unknown) {
      if (error instanceof NotAuthenticated) {
        setState({ status: 'signed-out', reason: error.message })
        return
      }
      setState({
        status: 'error',
        message: error instanceof Error ? error.message : 'Unknown error',
      })
    }
  }, [config.apiBaseUrl])

  useEffect(() => onSessionEnded(() => setSessionEnded(true)), [])

  /** Lands on a path without a reload, the way `open` does, minus the history entry. */
  const land = useCallback((path: string) => {
    window.history.replaceState({}, '', path)
    setView(viewFor(window.location.pathname))
  }, [])

  useEffect(() => {
    if (booted.current) return
    booted.current = true
    const run = async (): Promise<void> => {
      // Returning from Identity Center: finish the exchange, then replace the
      // callback URL with the page the sign-in began on, so a reload cannot
      // replay the code and the address bar carries no credential material.
      if (isCallback(window.location.search)) {
        setState({ status: 'signing-in' })
        try {
          const { returnTo } = await completeSignIn(
            config.auth,
            window.sessionStorage,
            window.location.search,
          )
          land(
            returnTo ??
              (window.location.pathname.startsWith(CALLBACK_PATH) ? '/' : window.location.pathname),
          )
          await load()
        } catch (error: unknown) {
          // Back to the page they were going to, so "Try again" returns there.
          land(error instanceof SignInFailed && error.returnTo !== undefined ? error.returnTo : '/')
          setState({
            status: 'error',
            message: error instanceof Error ? error.message : 'Sign-in failed',
          })
        }
        return
      }

      if (currentToken() === undefined) {
        /*
         * No token is the ordinary state of a reload or a deep link, because
         * tokens live in memory (IM-170). While Identity Center still has a
         * session this round trip asks nothing and lands back here. The guard
         * is what keeps a round trip that *cannot* succeed from repeating: an
         * automatic attempt that came back without a token says so instead.
         */
        if (!mayAttemptAutomatically(window.sessionStorage)) {
          setState({
            status: 'signed-out',
            reason: 'Signing in automatically did not work just now.',
          })
          return
        }
        setState({ status: 'signing-in' })
        redirect(
          await beginSignIn(config.auth, window.sessionStorage, {
            returnTo: hereForReturn(),
            automatic: true,
          }),
        )
        return
      }
      await load()
    }
    void run()
  }, [config.auth, load, land, redirect])

  /**
   * Loads whatever the current screen needs.
   *
   * Re-runs on navigation. A failure shows the shell with an error rather than
   * a blank page: the API returns the same 404 for a deployment out of scope as
   * for one that never existed, so "not found" is the only honest thing to say.
   */
  const role = state.status === 'signed-in' ? state.me.role : undefined
  /*
   * The secondary navigation for this view, computed once and used twice: the
   * loader skips a tab the viewer is not offered, and the render draws it.
   */
  const subnav = role === undefined ? undefined : subnavFor(view, role)

  useEffect(() => {
    if (state.status !== 'signed-in') {
      // So that signing back in blanks rather than showing the last session's
      // screen while the first fetch is in flight.
      loadedFor.current = undefined
      return
    }
    let live = true
    const load = async (): Promise<void> => {
      if (loadedFor.current !== view) setScreen({ of: 'loading' })
      loadedFor.current = view
      /*
       * A tab this role is not offered fetches nothing. Not a control — every
       * route checks regardless — but a read the API is certain to refuse is a
       * request, an audit record and an error panel for no answer.
       */
      if (subnavFor(view, state.me.role)?.blocked !== undefined) {
        if (live) setScreen({ of: 'unavailable' })
        return
      }
      try {
        if (view.screen === 'fleet') {
          /*
           * Each tab loads only its own data (the IM-83 rule, for tabs): the
           * Instances tab reads Qrvey's own fleet and no cost; the Cost tab
           * reads cost and the budget, and not the fleet.
           */
          const tab = view.tab ?? 'instances'
          switch (tab) {
            case 'instances':
              // One request, narrowed to Qrvey's own by the API — see `fleet.tsx`.
              if (live) setScreen({ of: 'fleet', fleet: await fetchQrveyFleet(config.apiBaseUrl) })
              return
            case 'cost':
              if (live) setScreen({ of: 'fleet-cost' })
              return
            default:
              return assertNever(tab)
          }
        }
        if (view.screen === 'customers') {
          const { tenants } = await fetchTenants(config.apiBaseUrl, showHidden)
          if (live) setScreen({ of: 'customers', tenants })
          return
        }
        if (view.screen === 'customer') {
          const tab = view.tab ?? 'overview'
          if (tab === 'overview') {
            // Together: neither is useful without the other, and two spinners on
            // one screen is worse than one slightly longer wait.
            const [accounts, deployments] = await Promise.all([
              fetchAccounts(config.apiBaseUrl, view.tenantId),
              fetchDeployments(config.apiBaseUrl, view.tenantId),
            ])
            if (live) {
              setScreen({
                of: 'customer',
                tenantId: view.tenantId,
                accounts: accounts.accounts,
                section: { tab, deployments: deployments.deployments },
              })
            }
            return
          }
          // Every other tab reads its own data when it mounts; the account list
          // is what answers 404 for a customer out of scope, as on Overview.
          const { accounts } = await fetchAccounts(config.apiBaseUrl, view.tenantId)
          if (live) {
            setScreen({ of: 'customer', tenantId: view.tenantId, accounts, section: { tab } })
          }
          return
        }
        if (view.screen === 'run') {
          const run = await fetchRun(config.apiBaseUrl, view.tenantId, view.runId)
          if (live) {
            setScreen({ of: 'run', tenantId: view.tenantId, runId: view.runId, run })
          }
          return
        }
        if (view.screen === 'audit') {
          // Loads and reloads itself as filters change, so there is nothing to
          // fetch before rendering it.
          if (live) setScreen({ of: 'audit' })
          return
        }
        if (view.screen === 'access') {
          if (live) setScreen({ of: 'access' })
          return
        }
        if (view.screen === 'account' && view.tab !== undefined && view.tab !== 'onboarding') {
          // The listing, not the assessment: this tab reads nothing from the
          // customer's account until something on it is asked for.
          const { accounts } = await fetchAccounts(config.apiBaseUrl, view.tenantId)
          const account = accounts.find((a) => a.accountKey === view.accountKey)
          if (!live) return
          setScreen(
            account === undefined
              ? { of: 'missing' }
              : { of: 'account-section', tenantId: view.tenantId, tab: view.tab, account },
          )
          return
        }
        if (view.screen === 'account') {
          const onboarding = await fetchAccountOnboarding(
            config.apiBaseUrl,
            view.tenantId,
            view.accountKey,
          )
          if (live) setScreen({ of: 'account', tenantId: view.tenantId, onboarding })
          return
        }
        const deployment = await fetchDeployment(
          config.apiBaseUrl,
          view.tenantId,
          view.deploymentId,
        )
        if (live) setScreen({ of: 'deployment', tenantId: view.tenantId, deployment })
      } catch (error: unknown) {
        if (!live) return
        if (error instanceof NotAuthenticated) {
          setState({ status: 'signed-out', reason: error.message })
          return
        }
        /*
         * A 404 is "not found"; anything else is not. The API deliberately
         * answers 404 for a tenant out of scope exactly as for one that never
         * existed, so that case must stay indistinguishable — but a 500 is
         * neither, and calling it "not found" sends someone to check a URL that
         * was fine.
         */
        setScreen(
          error instanceof ApiError && error.status !== 404
            ? { of: 'failed', status: error.status }
            : { of: 'missing' },
        )
      }
    }
    void load()
    return () => {
      live = false
    }
    // `state` for the role, which only changes on sign-in.
  }, [state, view, config.apiBaseUrl, reloadToken, showHidden])

  const signIn = useCallback(() => {
    void beginSignIn(config.auth, window.sessionStorage, { returnTo: hereForReturn() }).then(
      redirect,
    )
  }, [config.auth, redirect])

  /**
   * The trail to where you are — and only that.
   *
   * Until IM-73 this element also carried the links to Audit and Access, which
   * produced "Customers / Audit · Audit · Access" on the audit screen: the path
   * you took and the places you can go, interleaved. They are separate landmarks
   * now, and this one names no destination that is not above the current screen.
   */
  /*
   * The account an instance or a run is in, for its level of the trail
   * (IM-181) — from the record this screen already read, so it appears once
   * that read has landed and costs nothing. The key rather than the label:
   * the label is on the account listing, which neither screen reads, and a
   * fetch to name a breadcrumb would be the wrong trade.
   */
  const crumbAccount = ((): { tenantId: string; accountKey: string } | undefined => {
    if (
      view.screen === 'deployment' &&
      screen.of === 'deployment' &&
      screen.tenantId === view.tenantId &&
      screen.deployment.deploymentId === view.deploymentId
    ) {
      return { tenantId: view.tenantId, accountKey: screen.deployment.accountKey }
    }
    if (
      view.screen === 'run' &&
      screen.of === 'run' &&
      screen.tenantId === view.tenantId &&
      screen.runId === view.runId &&
      screen.run.accountKey !== undefined
    ) {
      return { tenantId: view.tenantId, accountKey: screen.run.accountKey }
    }
    return undefined
  })()

  const crumbs =
    // Nothing above the fleet, so there is no trail to draw: a breadcrumb
    // reading "Fleet" beside a nav item reading "Fleet" and a heading reading
    // "Fleet" is three of the same word.
    view.screen === 'fleet' ? null : (
      <nav aria-label="Breadcrumb" className="crumbs">
        <Link to={pathFor({ screen: 'fleet' })} onOpen={open}>
          Fleet
        </Link>
        {view.screen === 'customers' ? (
          <>
            <span className="sep">/</span>
            <span className="here">Customers</span>
          </>
        ) : (
          <>
            <span className="sep">/</span>
            <Link to={pathFor({ screen: 'customers' })} onOpen={open}>
              Customers
            </Link>
          </>
        )}
        {view.screen === 'audit' ||
        view.screen === 'access' ||
        view.screen === 'customers' ? null : (
          <>
            <span className="sep">/</span>
            {view.screen === 'customer' ? (
              <span className="here">{view.tenantId}</span>
            ) : (
              <Link to={pathFor({ screen: 'customer', tenantId: view.tenantId })} onOpen={open}>
                {view.tenantId}
              </Link>
            )}
          </>
        )}
        {crumbAccount === undefined ? null : (
          <>
            <span className="sep">/</span>
            <AccountLink
              tenantId={crumbAccount.tenantId}
              accountKey={crumbAccount.accountKey}
              role={role ?? ''}
              onOpen={open}
            />
          </>
        )}
        {view.screen === 'deployment' ? (
          <>
            <span className="sep">/</span>
            <span className="here">
              {view.deploymentId}
              {/* The name, so the lock (IM-174) — from the record this screen
                already read, once it has; nothing while it loads. */}
              {screen.of === 'deployment' &&
              screen.deployment.deploymentId === view.deploymentId ? (
                <ProtectionLock
                  mark={screen.deployment.protected}
                  kind={jumpTenants.find((t) => t.tenantId === view.tenantId)?.kind}
                />
              ) : null}
            </span>
          </>
        ) : null}
        {view.screen === 'run' ? (
          <>
            <span className="sep">/</span>
            <span className="here">{view.runId}</span>
          </>
        ) : null}
        {view.screen === 'account' ? (
          <>
            <span className="sep">/</span>
            <span className="here">{view.accountKey}</span>
          </>
        ) : null}
        {view.screen === 'audit' ? (
          <>
            <span className="sep">/</span>
            <span className="here">Audit</span>
          </>
        ) : null}
        {view.screen === 'access' ? (
          <>
            <span className="sep">/</span>
            <span className="here">Access</span>
          </>
        ) : null}
      </nav>
    )

  return (
    <>
      <header className="app-bar">
        <h1 className="brand">
          <span className="brand-mark" aria-hidden="true" />
          Qrvey Instance Manager
        </h1>
        {/* Which environment this is, said where nobody has to look for it: the
          same console serves dev and production, and that word is the whole
          difference between a harmless apply and a serious one. */}
        <p className="env-chip">{config.environment}</p>

        {state.status === 'signed-in' ? (
          <>
            <nav aria-label="Main" className="main-nav">
              <Link
                to={pathFor({ screen: 'fleet' })}
                onOpen={open}
                {...(view.screen === 'fleet' ? { 'aria-current': 'page' as const } : {})}
              >
                Fleet
              </Link>
              <Link
                to={pathFor({ screen: 'customers' })}
                onOpen={open}
                {...(view.screen === 'customers' ? { 'aria-current': 'page' as const } : {})}
              >
                Customers
              </Link>
              {/* Offered only to a role that holds audit:read. Not a control —
                the API refuses either way — but a link that always 403s is a
                worse way to say no than no link. */}
              {isRole(state.me.role) && roleHasCapability(state.me.role, 'audit:read') ? (
                <Link
                  to={pathFor({ screen: 'audit' })}
                  onOpen={open}
                  {...(view.screen === 'audit' ? { 'aria-current': 'page' as const } : {})}
                >
                  Audit
                </Link>
              ) : null}
              {/* Same reasoning, for access:manage. */}
              {isRole(state.me.role) && roleHasCapability(state.me.role, 'access:manage') ? (
                <Link
                  to={pathFor({ screen: 'access' })}
                  onOpen={open}
                  {...(view.screen === 'access' ? { 'aria-current': 'page' as const } : {})}
                >
                  Access
                </Link>
              ) : null}
            </nav>
            {/* Moved out of the footer: who you are signed in as and what you
              can see is context for reading the screen, not a postscript to it.
              This is how someone notices they are the wrong person for the job
              in front of them. */}
            {/* Discoverable, not just documented: a shortcut nobody knows
              about is a shortcut nobody uses. */}
            <button
              type="button"
              className="jump"
              onClick={() => {
                cameFrom.current =
                  document.activeElement instanceof HTMLElement ? document.activeElement : null
                setPaletteOpen(true)
              }}
            >
              Jump to…<kbd>⌘K</kbd>
            </button>
            <p className="who">
              <strong>{state.me.email}</strong>
              {state.me.role} · {describeScope(state.me.scope)}
            </p>
            {/* In the header because it is true of every screen, and because
              the alternative was the next click failing with nothing said
              (IM-170). Signing in comes back to this page. */}
            {sessionEnded ? (
              <p role="alert" className="session-ended">
                Your session has ended.
                <button type="button" onClick={signIn}>
                  Sign in
                </button>
              </p>
            ) : null}
          </>
        ) : null}
      </header>

      <main>
        {state.status === 'starting' || state.status === 'signing-in' ? (
          <p role="status">Signing in…</p>
        ) : null}

        {state.status === 'signed-out' ? (
          <section className="panel">
            <h2>Sign in</h2>
            {state.reason === undefined ? null : <p role="alert">{state.reason}</p>}
            <p>You need to sign in with your Qrvey account.</p>
            <button type="button" onClick={signIn}>
              Sign in
            </button>
          </section>
        ) : null}

        {state.status === 'error' ? (
          <section className="panel">
            <h2>Sign-in failed</h2>
            <p role="alert">{state.message}</p>
            <button type="button" onClick={signIn}>
              Try again
            </button>
          </section>
        ) : null}

        {state.status === 'signed-in' ? (
          <>
            {paletteOpen ? (
              <Palette
                fleet={jumpFleet}
                tenants={jumpTenants}
                /* The open customer's accounts, which its screen already read —
                  so a new account with no instances is reachable too (IM-181),
                  and nothing is fetched for it. */
                {...(screen.of === 'customer'
                  ? { accounts: { tenantId: screen.tenantId, accounts: screen.accounts } }
                  : {})}
                onOpen={open}
                onClose={() => setPaletteOpen(false)}
              />
            ) : null}

            {crumbs}

            {/* The screen's tabs, below the trail and above the content. Not
              drawn over "not found" or a failure: a tab bar there offers more
              ways to reach the same answer. */}
            {subnav === undefined || screen.of === 'missing' || screen.of === 'failed' ? null : (
              <Subnav model={subnav} onOpen={open} />
            )}

            {screen.of === 'unavailable' && subnav?.blocked !== undefined ? (
              <TabUnavailable
                needs={subnav.blocked.needs}
                fallback={subnav.blocked.fallback}
                onOpen={open}
              />
            ) : null}

            {screen.of === 'loading' ? <p role="status">Loading…</p> : null}

            {screen.of === 'failed' ? (
              <p role="alert">
                The link is fine — something went wrong answering it ({String(screen.status)}). Try
                again; if it keeps happening the API log has the reason.
              </p>
            ) : null}

            {screen.of === 'missing' ? (
              // Deliberately not "you do not have access": the API cannot tell
              // us which it is, and guessing would confirm that another
              // customer's deployment exists.
              <p role="alert">Not found. It may have been removed, or the link may be wrong.</p>
            ) : null}

            {screen.of === 'fleet' ? (
              <Fleet fleet={screen.fleet} onOpen={open} role={state.me.role} />
            ) : null}

            {screen.of === 'fleet-cost' ? (
              <FleetCostTab baseUrl={config.apiBaseUrl} role={state.me.role} />
            ) : null}

            {screen.of === 'customers' ? (
              <Customers
                baseUrl={config.apiBaseUrl}
                me={state.me}
                tenants={screen.tenants}
                onOpen={open}
                onChanged={reload}
                showHidden={showHidden}
                onShowHidden={setShowHidden}
                environment={config.environment}
              />
            ) : null}

            {screen.of === 'customer'
              ? (() => {
                  const { section } = screen
                  const viewer = { email: state.me.email, isStaff: state.me.isStaff }
                  switch (section.tab) {
                    case 'overview':
                      return (
                        <Customer
                          baseUrl={config.apiBaseUrl}
                          tenantId={screen.tenantId}
                          kind={jumpTenants.find((t) => t.tenantId === screen.tenantId)?.kind}
                          role={state.me.role}
                          environment={config.environment}
                          accounts={screen.accounts}
                          deployments={section.deployments}
                          onOpen={open}
                          onChanged={reload}
                          viewer={viewer}
                        />
                      )
                    case 'approvals':
                      // What is waiting for somebody to agree (IM-102, IM-39) —
                      // for a customer, the queue they approve from and the
                      // setting for what never waits.
                      return (
                        <ApprovalsPanel
                          baseUrl={config.apiBaseUrl}
                          tenantId={screen.tenantId}
                          kind={jumpTenants.find((t) => t.tenantId === screen.tenantId)?.kind}
                          viewer={{ role: state.me.role, ...viewer }}
                          onOpen={open}
                          onChanged={reload}
                          standalone
                        />
                      )
                    case 'users':
                      // The customer's own people (IM-153).
                      return (
                        <CustomerUsersPanel
                          baseUrl={config.apiBaseUrl}
                          tenantId={screen.tenantId}
                          role={state.me.role}
                        />
                      )
                    case 'cost':
                      return (
                        <CustomerCost
                          baseUrl={config.apiBaseUrl}
                          tenantId={screen.tenantId}
                          role={state.me.role}
                          accounts={screen.accounts}
                        />
                      )
                    case 'audit':
                      return <Audit baseUrl={config.apiBaseUrl} tenantId={screen.tenantId} />
                    default:
                      return assertNever(section)
                  }
                })()
              : null}

            {screen.of === 'run' ? (
              <Run
                baseUrl={config.apiBaseUrl}
                tenantId={screen.tenantId}
                runId={screen.runId}
                initial={screen.run}
                role={state.me.role}
                onOpen={open}
              />
            ) : null}

            {screen.of === 'audit' ? <Audit baseUrl={config.apiBaseUrl} /> : null}

            {screen.of === 'access' ? <Access baseUrl={config.apiBaseUrl} /> : null}

            {screen.of === 'account' ? (
              <Account
                baseUrl={config.apiBaseUrl}
                tenantId={screen.tenantId}
                onboarding={screen.onboarding}
                role={state.me.role}
                onChanged={reload}
              />
            ) : null}

            {screen.of === 'account-section' ? (
              <AccountSection
                tab={screen.tab}
                baseUrl={config.apiBaseUrl}
                tenantId={screen.tenantId}
                account={screen.account}
                kind={jumpTenants.find((t) => t.tenantId === screen.tenantId)?.kind}
                role={state.me.role}
                onChanged={reload}
              />
            ) : null}

            {screen.of === 'deployment' ? (
              <Deployment
                baseUrl={config.apiBaseUrl}
                tenantId={screen.tenantId}
                role={state.me.role}
                environment={config.environment}
                deployment={screen.deployment}
                onOpen={open}
                onChanged={reload}
                kind={jumpTenants.find((t) => t.tenantId === screen.tenantId)?.kind}
                requesterIsStaff={state.me.isStaff}
              />
            ) : null}
          </>
        ) : null}
      </main>
    </>
  )
}
